import { DataResult, State, process } from '@progress/kendo-data-query';
import { Component, Renderer2, NgZone, AfterViewInit, OnInit, DoCheck, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { tap, take } from 'rxjs/operators';
import { CancelEvent, GridComponent, GridDataResult, RemoveEvent, RowClassArgs, SaveEvent } from '@progress/kendo-angular-grid';
import { Router } from '@angular/router';
import { ChecklistService } from '../shared/services/checklist.service';
import { ChecklistResponseModel } from '../shared/models/checklistResponseModel';
import { ChecklistItemReuqestModel } from '../shared/models/checklistItemRequestModel';
import { DirtyFormChecker } from 'src/app/shared/routeguards/DirtyFormChecker';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from 'src/app/shared/services/toast.service';
import { Store } from '@ngrx/store';
import { GetUserPermissionList } from 'src/app/state';
import { TranslationLoaderFacade } from '../../localization/translation-loader.facade';
import { Language } from 'src/app/master/models/language';
import { MasterLookupService } from 'src/app/master/master-services/master-lookup.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalizedTextModel } from '../../product-services/shared/models/LocalizedTextModel';

const tableRow = (node: any)  => node.tagName.toLowerCase() === 'tr';

const closest = (node: any, predicate: any) => {
    while (node && !predicate(node)) {
        node = node.parentNode;
    }
    return node;
};

const createFormGroup = (dataItem: any) => {
  const isEnglish = dataItem.language.languageId === 1;
  return new FormGroup({
    language: new FormControl(dataItem.language, Validators.required),
    checklistItem: new FormControl(
      dataItem.checklistItem, 
      isEnglish
        ? [Validators.required, Validators.maxLength(60), Validators.pattern(/^(?!\s*$)((?!<)(?!>)(?![&]).)*$/)]
        : [Validators.maxLength(60), Validators.pattern(/^(?!\s*$)((?!<)(?!>)(?![&]).)*$/)]
    ),
    linkName: new FormControl(dataItem.linkName, Validators.pattern(/^(?!\s*$)((?!<)(?!>)(?![&]).)*$/)),
    link: new FormControl(dataItem.link, [Validators.pattern(/(https?:\/\/)?[a-zA-Z]{1,3}[.]?[-a-zA-Z0-9@:%._\+~#=]{1,247}[.][a-z]{1,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/), Validators.maxLength(250)]),
    checklistId: new FormControl(dataItem.checklistId),
    sortValue: new FormControl(dataItem.sortValue)
  });
};

@Component({
  selector: 'app-add-checklist',
  templateUrl: './add-checklist.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./add-checklist.component.scss']
})

export class AddChecklistComponent implements OnInit, AfterViewInit, DoCheck, OnDestroy, DirtyFormChecker {
  public state: State = {
    skip: 0,
    take: 10
  };
  public formGroup?: FormGroup;
  public gridData: DataResult = process([], this.state);
  public serviceName: string = '';
  public addStatus: string = 'Add';
  public leaveOpened: boolean = false;
  public addingNewItem: boolean = false;
  public editingItem: boolean = false;
  public opened: boolean = false;
  public checklistToRemove = {checklistId: 0, checklistItem: ''};
  private currentSubscription: Subscription = new Subscription();
  public editedRowIndex: number = -1;
  private editedChecklist: ChecklistResponseModel = new ChecklistResponseModel();
  public draggable: boolean = false;
  public userPermissionList: number[] = [];
  public languages: Language[] = [];
  private checklistItemLocalizedFieldId = 13;
  private linkNameLocalizedFieldId = 14;
  private tempCheckListItemLocalizedText: LocalizedTextModel[] = [];
  private tempLinkNameLocalizedText: LocalizedTextModel[] = [];

  constructor(private renderer: Renderer2, private zone: NgZone, 
    private router: Router, public editService: ChecklistService,
    public toastService: ToastService, private store: Store<State>, 
    private translationLoaderFacade: TranslationLoaderFacade, 
    private lookupService: MasterLookupService, 
    private translateService: TranslateService) { }

  hasUnsavedData(): boolean {
    return this.editedRowIndex !== -1;
  }

  public ngOnInit(): void {
    this.store.select(GetUserPermissionList).subscribe((allUserPermissions) => {
      if(!allUserPermissions) {
        throw Error('allUserPermissions is undefined!');
      }
      this.userPermissionList = allUserPermissions;
    });
    this.addStatus = this.editService.getIsEdit() ? 'Edit' : 'Add';
    this.serviceName = this.editService.getServiceName();
    this.loadLanguages();
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.editService.read().subscribe((data: ChecklistResponseModel[]) => {
        // Set default language to English for all items if not set
        const englishLang = this.languages.find(l => l.languageId === 1);
        data.forEach(item => {
          if (!item.language) {
            item.language = englishLang;
          }
        });

        (data.length > 1) ? this.draggable = true : this.draggable = false;
        this.gridData = process(data, this.state);
        setTimeout(() => {
          if(this.draggable) {
            this.currentSubscription = this.handleDragAndDrop();
          }
        }, 1000);
      });
    });
  }

  public ngOnDestroy(): void {
    if (this.currentSubscription !== undefined) {
      this.currentSubscription.unsubscribe();
    }
  }

  public ngDoCheck(): void {
    (this.gridData.total > 1) ? this.draggable = true : this.draggable = false;
  }

  public dataStateChange(state: State): void {
      this.state = state;
      this.readData();
  }
 
  public rowCallback(context: RowClassArgs) {
    return {
        dragging: context.dataItem.dragging
    };
  }

  public addHandler(sender: GridComponent) {
    if (this.editedRowIndex === -1) {
      this.closeEditor(sender);
      this.addingNewItem = true;

      const dataItem = new ChecklistResponseModel();
      dataItem.checklistId = 0;
      dataItem.checklistItem = '';
      dataItem.linkName = '';
      dataItem.link = '';
      dataItem.language = this.languages.find(l => l.languageId === 1) || this.languages[0];
      dataItem._prevLanguageId = 1;
      this.tempCheckListItemLocalizedText = [];
      this.tempLinkNameLocalizedText = [];

      const rowIndex = this.gridData.data.length;
      this.gridData.data.splice(rowIndex, 0, dataItem);
      this.editHandler({sender, rowIndex, dataItem});
    }
  }

  public editHandler({sender, rowIndex, dataItem}: {sender: GridComponent, rowIndex: number, dataItem: ChecklistResponseModel}) {
    this.closeEditor(sender);

    this.editingItem = true;
    this.DisableDragAndDrop();
    dataItem.isEditing = true;
    dataItem._prevLanguageId = 1;
    this.formGroup = createFormGroup(dataItem);
    this.tempCheckListItemLocalizedText = dataItem.localizedChecklistItem ? [...dataItem.localizedChecklistItem] : [];
    this.tempLinkNameLocalizedText = dataItem.localizedLinkName ? [...dataItem.localizedLinkName] : [];

    this.editedRowIndex = rowIndex;
    this.editedChecklist = Object.assign({}, dataItem);
    
    sender.editRow(rowIndex, this.formGroup);
  }

  public cancelHandler(event: CancelEvent) {
    const dataItem = event.formGroup.value;  
    if (this.addingNewItem || this.editingItem) {
      this.addingNewItem = false;
      this.editingItem = false;
      this.EnableDragAndDrop();
    }
    if(dataItem.checklistId==0){
      this.gridData.data.splice(event.rowIndex, 1);
    } 

    this.tempCheckListItemLocalizedText = [];
    this.tempLinkNameLocalizedText = [];
    this.closeEditor(event.sender, event.rowIndex);
  }

  public saveHandler(event: SaveEvent) {
    const dataItem = event.formGroup.value;
    this._saveLocalizedFieldsValues(event.dataItem._prevLanguageId, event.dataItem);
    dataItem.isEditing = false;
    const checklistRequest = new ChecklistItemReuqestModel();
    checklistRequest.checklistItem = this.tempCheckListItemLocalizedText.find(item => item.languageId === 1)?.text || '';
    checklistRequest.linkName = this.tempLinkNameLocalizedText.find(item => item.languageId === 1)?.text || '';
    checklistRequest.link = dataItem.link.replace(/ /g, '');
    checklistRequest.checklistId = dataItem.checklistId;
    checklistRequest.sortValue = dataItem.sortValue;
    checklistRequest.localizedChecklistItem = this.tempCheckListItemLocalizedText;
    checklistRequest.localizedLinkName = this.tempLinkNameLocalizedText;
    checklistRequest.localizedRowId = event.dataItem.localizedRowId;
    let isSaving=true;

    console.log(event);
    this.editService.save(checklistRequest, this.addingNewItem).subscribe({
      next: () => {
        this.readData(false,isSaving);
        if (this.addingNewItem) {
          this.toastService.showSuccess('_Checklist_Checklist_Created_Successfully_Ts_Text_');
        } else {
          this.toastService.showSuccess('_General_Changes_Saved_Successfully_Ts_Text_');
        }
      },
      error: () => {
        this.readData(false,isSaving);
        if (this.addingNewItem) {
          this.toastService.showError('_Checklist_Checklist_Creation_Failed_Ts_Text_');
        } else {
        this.toastService.showError('_General_Saving_Failed_Ts_Text_');
        }
      }
    });            
    event.sender.closeRow(event.rowIndex);
    this.editedRowIndex = -1;
    this.editedChecklist = new ChecklistResponseModel();
  }

  public removeHandler(event: RemoveEvent) {
    this.checklistToRemove = { checklistId: event.dataItem.checklistId, checklistItem: event.dataItem.checklistItem};
    this.opened = true;
  }

  public backToServicePage() {
    this.router.navigate(['admin/checklist']);
  }

  public close(status: string) {
    this.opened = false;
    if (status === 'Yes') {
      this.editService.remove(this.checklistToRemove.checklistId).subscribe({
        next: () => {
          this.readData(true);
          this.toastService.showSuccess('_Checklist_Checklist_Item_Deleted_Successfully_Ts_Text_')
        },
        error: () => {
          this.toastService.showError('_Checklist_Checklist_Item_Deletion_Failed_Ts_Text_')
        }
      });
    } else {
      this.checklistToRemove = {checklistId: 0, checklistItem: ''};
    }
  }

  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editService.resetItem(this.editedChecklist);
    this.editedRowIndex = -1;
    this.editedChecklist = new ChecklistResponseModel();
    this.formGroup = undefined;
  }

  private readData(reorderAfterRead?: boolean,isSaving? : boolean) {
    this.ngOnDestroy();
    this.editService.read().subscribe((data: any[]) => {
      // Set default language to English for all items if not set
      const englishLang = this.languages.find(l => l.languageId === 1);
      data.forEach(item => {
        if (!item.language) {
          item.language = englishLang;
        }
      });
      this.checklistToRemove = {checklistId: 0, checklistItem: ''};
      this.gridData = <GridDataResult>{};
      this.formGroup=undefined;
      this.gridData = process(data, this.state);
      setTimeout(() => {
        this.zone.onStable.pipe(take(1)).subscribe(() => {
          this.currentSubscription = this.handleDragAndDrop();
          if (reorderAfterRead) {
            this.rearrangeGrid();
          }
      });
      }, 1000);

    }).add(() => {
      if(isSaving){
      if (this.addingNewItem || this.editingItem) {
        this.addingNewItem = false;
        this.editingItem = false;
        this.EnableDragAndDrop();    
        }                                                           
      }
  }); 
  }

  private rearrangeGrid() {
    console.log(this.gridData.data);
    const requestData = this.gridData.data.map((dataItem, index: number) => {
      const newIndex = index + 1;
      dataItem.sortValue = newIndex;

      const checklistRequest = new ChecklistItemReuqestModel();
      checklistRequest.checklistItem = dataItem.checklistItem;
      checklistRequest.linkName = dataItem.linkName;
      checklistRequest.link = dataItem.link;
      checklistRequest.checklistId = dataItem.checklistId;
      checklistRequest.sortValue = newIndex;
      checklistRequest.localizedRowId = dataItem.localizedRowId;
      checklistRequest.localizedChecklistItem = dataItem.localizedChecklistItem;
      checklistRequest.localizedLinkName = dataItem.localizedLinkName;
      return checklistRequest;
    });

    this.editService.rearrange(requestData).subscribe({
      next: () => {},
      error: () => this.readData()
    });
  }

  private handleDragAndDrop(): Subscription {
    const sub = new Subscription(() => {});
    let draggedItemIndex: number = -1;

    const tableRows = Array.from(document.querySelectorAll('.k-grid tr'));

    tableRows.forEach(item => {
        this.renderer.setAttribute(item, 'draggable', 'true');
        const dragStart = fromEvent<DragEvent>(item, 'dragstart');
        const dragOver = fromEvent(item, 'dragover');
        const dragEnd = fromEvent(item, 'dragend');

        sub.add(dragStart.pipe(
            tap(({ dataTransfer }) => {
                try {
                  var ESAPI = require('node-esapi');
                  const dragImgEl = document.createElement('span');
                  var dragImgElStyle='position: absolute; display: block; top: 0; left: 0; width: 0; height: 0;';
                  dragImgEl.setAttribute('style', '<%=ESAPI.encoder().encodeForJavascript(ESAPI.encoder().encodeForHTMLAttribute(dragImgElStyle))%>');
                  document.body.appendChild(dragImgEl);
                  dataTransfer?.setDragImage(dragImgEl, 0, 0);
                } catch (err) {
                  // IE doesn't support setDragImage
                }
                try {
                    // Firefox won't drag without setting data
                    dataTransfer?.setData('application/json', '{}');
                } catch (err) {
                    // IE doesn't support MIME types in setData
                }
            })
        ).subscribe(({ target }) => {
            const row: HTMLTableRowElement = target as HTMLTableRowElement;
            draggedItemIndex = row.rowIndex;
            const dataItem = this.gridData.data[draggedItemIndex];
            dataItem.dragging = true;
        }));

        sub.add(dragOver.subscribe((e: any) => {
            e.preventDefault();
            if(draggedItemIndex != -1){
              const dataItem = this.gridData.data.splice(draggedItemIndex, 1)[0];
              const dropIndex = closest(e.target, tableRow).rowIndex;
              draggedItemIndex = dropIndex;
              this.zone.run(() =>
                 this.gridData.data.splice(dropIndex, 0, dataItem)
              );
            }
        }));

        sub.add(dragEnd.subscribe((e: any) => {
            e.preventDefault();
            let allGridData=this.gridData.data;
             allGridData.map((item: any, index: number)=>{
              item.dragging = false;
              })
            const dataItem = this.gridData.data[draggedItemIndex];
            dataItem.dragging = false;
            this.rearrangeGrid();
        }));
    });
    return sub;
  }

  private DisableDragAndDrop() {
    const tableRows = Array.from(document.querySelectorAll('.k-grid tr'));

    tableRows.forEach(item => {
      this.renderer.setAttribute(item, 'draggable', 'false');
    });

  }
  private EnableDragAndDrop() {
    const tableRows = Array.from(document.querySelectorAll('.k-grid tr'));

    tableRows.forEach(item => {
      this.renderer.setAttribute(item, 'draggable', 'true');
    });

  }

  public onLanguageChange(dataItem: ChecklistResponseModel) {
    const selectedLang = dataItem.language?.languageId;
    const prevLang = dataItem._prevLanguageId;
    this._saveLocalizedFieldsValues(prevLang, dataItem);

    // Clear fields and update validators for Spanish
    if (selectedLang === 3) {
      this.formGroup?.controls.checklistItem.clearValidators();
      this.formGroup?.controls.checklistItem.setValidators([
        Validators.maxLength(60),
        Validators.pattern(/^(?!\s*$)((?!<)(?!>)(?![&]).)*$/)
      ]);
      this.formGroup?.controls.checklistItem.updateValueAndValidity();

      this.formGroup?.controls.linkName.clearValidators();
      this.formGroup?.controls.linkName.setValidators([
        Validators.pattern(/^(?!\s*$)((?!<)(?!>)(?![&]).)*$/)
      ]);
      this.formGroup?.controls.linkName.updateValueAndValidity();
    } else if (selectedLang === 1) {
      this.formGroup?.controls.checklistItem.setValidators([
        Validators.required,
        Validators.maxLength(60),
        Validators.pattern(/^(?!\s*$)((?!<)(?!>)(?![&]).)*$/)
      ]);
      this.formGroup?.controls.checklistItem.updateValueAndValidity();

      this.formGroup?.controls.linkName.setValidators([
        Validators.pattern(/^(?!\s*$)((?!<)(?!>)(?![&]).)*$/)
      ]);
      this.formGroup?.controls.linkName.updateValueAndValidity();
    }

    this._changeLocalizedFieldsValues(selectedLang, dataItem);
    dataItem._prevLanguageId = selectedLang || 1;
  }

  private loadLanguages() {
    this.lookupService.getLanguages().subscribe({
      next: (languages) => {
        languages.forEach(lan =>{
          if(lan.languageCode == 'en-US') {
            lan.languageName = this.translateService.instant('_General_English_EN_Text_');
          }
          else if(lan.languageCode == 'es-mx') {
            lan.languageName = this.translateService.instant('_General_Spanish_ES_Text_');
          }
        })
        this.languages = languages;
      },
      error: (err) => { console.log(err); }
    });
  }

  public languageTrackBy(index: number, item: Language) {
    return item.languageId;
  }

  private _saveLocalizedFieldsValues(lanId: number | undefined, checklist: ChecklistResponseModel) {
    var added = false;
    for (let i = 0; i < this.tempCheckListItemLocalizedText.length; i++) {
      if (this.tempCheckListItemLocalizedText[i].languageId == lanId) {
        this.tempCheckListItemLocalizedText[i].text = this.formGroup?.controls.checklistItem.value ?? "";
        added = true;
      }
    }
    if (!added) {
      this.tempCheckListItemLocalizedText.push({
        localizedTextId: 0,
        localizedRowId: checklist.localizedRowId,
        languageId: lanId,
        localizedFieldId: this.checklistItemLocalizedFieldId,
        text: this.formGroup?.controls.checklistItem.value ?? ""
      } as LocalizedTextModel);
    }
    added = false;
    for (let i = 0; i < this.tempLinkNameLocalizedText.length; i++) {
      if (this.tempLinkNameLocalizedText[i].languageId == lanId) {
        this.tempLinkNameLocalizedText[i].text = this.formGroup?.controls.linkName.value ?? "";
        added = true;
      }
    }
    if (!added) {
      this.tempLinkNameLocalizedText.push({
        localizedTextId: 0,
        localizedRowId: checklist.localizedRowId,
        languageId: lanId,
        localizedFieldId: this.linkNameLocalizedFieldId,
        text: this.formGroup?.controls.linkName.value ?? ""
      } as LocalizedTextModel);
    }
  }

  private _changeLocalizedFieldsValues(newLanguageId: number | undefined, checklist: ChecklistResponseModel) {
    // Find localized values for the selected language
    const localizedChecklist = this.tempCheckListItemLocalizedText.find(l => l.languageId === newLanguageId);
    const localizedLinkName = this.tempLinkNameLocalizedText.find(l => l.languageId === newLanguageId);

    // Assign values to the fields
    this.formGroup?.controls.checklistItem.setValue(localizedChecklist ? localizedChecklist.text : '');
    this.formGroup?.controls.linkName.setValue(localizedLinkName ? localizedLinkName.text : '');
  }

  public isEnglishCheckListItemValueEntered(): boolean {
    return (this.formGroup?.controls.language.value.languageId === 1 && 
      (this.formGroup?.controls.checklistItem.value || this.formGroup?.controls.checklistItem.value.trim() != '')) ||
      (this.tempCheckListItemLocalizedText.find(item => item.languageId === 1)?.text || 
      this.tempCheckListItemLocalizedText.find(item => item.languageId === 1)?.text.trim() != '');
  }
}
